import {
	Badge,
	Box,
	CircularProgress,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
} from "@mui/material";
import React from "react";

import LogoutIcon from "@mui/icons-material/Logout";
import FeedbackIcon from "@mui/icons-material/Feedback";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import "./DrawerNavigation.css";
import { useDrawerNavigationViewModel, type TopItem } from "./DrawerNavigation.vm";
import { type NavLink } from "./navLinks";
import { VisuallyHidden } from "@gui/widgets/A11y";
import { ISSUE_COUNT_LOADING_LABEL, issueCountBadgeLabel } from "@gui/contexts/issueCounts/issueCountLabels";
import { useResponsiveView } from "@gui/hooks/useResponsiveView";
export type { NavLink } from "./navLinks";

export const SIDEBAR_COLLAPSED_WIDTH = 60;
const MORE_BUTTON_HEIGHT = 48;

const ItemBreak = React.memo(function ItemBreak() {
	return <ListItem className="sidebar-item-break" />;
});

type BadgeLike = { isFetching: boolean; showCountBadge: boolean; count?: number };

/**
 * Placement for a count badge anchored on a 24px square icon (the nav rail, the Home tiles).
 *
 * MUI centres the badge on the icon's corner, and `overlap="circular"` pulls it a further 14%
 * inward — right for an avatar, but on a 24px glyph a two-digit badge covered a third of it
 * (design-audit live pass: the Job Errors tile and the rail's bug icon). Nudging the anchor a
 * few pixels outward leaves the badge touching the glyph's corner instead of sitting on it.
 */
export const ICON_COUNT_BADGE_SX = { "& .MuiBadge-badge": { top: -3, right: -3 } } as const;

// Wrap an icon in the error-colored issue-count badge (or a spinner while fetching). Shared by the
// per-item nav rows, the "More" overflow button, and the collapsible submenu button.
//
// The badge is hidden from the accessibility tree and its meaning restated as text alongside it:
// the spinner conveys nothing there at all, and a bare count reads as "Job Errors 6" with no
// indication of what the 6 counts. Same treatment as Home's card grid, sharing its label helpers.
function decorateIconWithBadge(rawIcon: React.ReactNode, badge: BadgeLike): React.ReactNode {
	if (badge.isFetching) {
		return (
			<>
				<Badge
					badgeContent={<CircularProgress size={10} thickness={6} sx={{ color: "inherit" }} />}
					color="error"
					sx={ICON_COUNT_BADGE_SX}
					aria-hidden="true"
				>
					{rawIcon}
				</Badge>
				<VisuallyHidden>{ISSUE_COUNT_LOADING_LABEL}</VisuallyHidden>
			</>
		);
	}
	if (badge.showCountBadge) {
		return (
			<>
				<Badge badgeContent={badge.count} color="error" sx={ICON_COUNT_BADGE_SX} aria-hidden="true">
					{rawIcon}
				</Badge>
				<VisuallyHidden>{issueCountBadgeLabel(badge.count ?? 0)}</VisuallyHidden>
			</>
		);
	}
	return rawIcon;
}

// A sidebar row that opens a dropdown of nav links instead of navigating itself (e.g. Developer).
// Owns its own anchor state so multiple submenus stay independent.
function SidebarSubmenu({
	label,
	icon,
	links,
	showExpanded,
	renderMenuItem,
}: {
	label: string;
	icon: React.ReactNode;
	links: NavLink[];
	showExpanded: boolean;
	renderMenuItem: (link: NavLink, close: () => void) => React.ReactNode;
}) {
	const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
	const close = () => setAnchor(null);
	return (
		<>
			<ListItem disablePadding>
				<Tooltip title={showExpanded ? "" : label} placement="right">
					<ListItemButton onClick={(e) => setAnchor(e.currentTarget)}>
						<ListItemIcon>{icon}</ListItemIcon>
						<ListItemText primary={label} />
					</ListItemButton>
				</Tooltip>
			</ListItem>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={close}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				{links.map((link) => renderMenuItem(link, close))}
			</Menu>
		</>
	);
}

interface DrawerNavigationProps {
	onNavigate: (link: NavLink) => void;
	mobileOpen: boolean;
	onMobileClose: () => void;
}

export const DrawerNavigation = (props: DrawerNavigationProps) => {
	const { isMobile } = useResponsiveView();
	const [expanded, setExpanded] = React.useState(false);
	const [moreAnchor, setMoreAnchor] = React.useState<HTMLElement | null>(null);
	const [overflowIndex, setOverflowIndex] = React.useState<number | null>(null);

	const contentRef = React.useRef<HTMLDivElement>(null);
	const ghostRef = React.useRef<HTMLUListElement>(null);
	const bottomRef = React.useRef<HTMLDivElement>(null);

	const closeMenusOnNav = React.useCallback(() => {
		if (isMobile) props.onMobileClose();
		setMoreAnchor(null);
		setExpanded(false);
	}, [isMobile, props]);

	const vm = useDrawerNavigationViewModel({
		onNavigate: props.onNavigate,
		mobileOpen: props.mobileOpen,
		onMobileClose: props.onMobileClose,
		isMobile,
		closeMenusOnNav,
	});

	React.useLayoutEffect(() => {
		if (isMobile) {
			setOverflowIndex(null);
			return;
		}
		const paperEl = contentRef.current?.closest(".MuiDrawer-paper") as HTMLElement | null;
		const ghost = ghostRef.current;
		const bottom = bottomRef.current;
		if (!paperEl || !ghost || !bottom) return;

		const compute = () => {
			const available = paperEl.clientHeight - bottom.offsetHeight;
			if (available <= 0) return;

			if (ghost.scrollHeight <= available) {
				setOverflowIndex(null);
				return;
			}

			const budget = available - MORE_BUTTON_HEIGHT;
			const children = Array.from(ghost.children) as HTMLElement[];
			const ghostTop = ghost.offsetTop;
			let cutoff = children.length;
			for (let i = 0; i < children.length; i++) {
				const child = children[i];
				const bottomEdge = child.offsetTop + child.offsetHeight - ghostTop;
				if (bottomEdge > budget) {
					cutoff = i;
					break;
				}
			}
			while (cutoff > 0 && vm.topItems[cutoff - 1]?.kind === "divider") cutoff--;
			setOverflowIndex(cutoff);
		};

		compute();
		const ro = new ResizeObserver(compute);
		ro.observe(paperEl);
		ro.observe(bottom);
		return () => ro.disconnect();
	}, [isMobile, vm.topItems]);

	const showExpanded = isMobile || expanded;
	const drawerClass = `sidebar-drawer${showExpanded ? " expanded" : ""}`;

	const renderItem = (item: TopItem, inMenu: boolean): React.ReactNode => {
		if (item.kind === "divider") {
			return inMenu ? null : <ItemBreak key={item.key} />;
		}

		if (item.kind === "submenu") {
			const navItems: TopItem[] = item.links.map((link) => ({ kind: "nav", key: link.to, link }));
			// Already inside the "More" overflow menu — flatten the links rather than nest a menu.
			if (inMenu) return navItems.map((n) => renderItem(n, true));
			// Roll the links' issue counts up onto the group button so a pending badge shows while collapsed.
			const groupBadge = vm.getOverflowBadgeState(navItems);
			return (
				<SidebarSubmenu
					key={item.key}
					label={item.label}
					icon={decorateIconWithBadge(item.icon, groupBadge)}
					links={item.links}
					showExpanded={showExpanded}
					renderMenuItem={(link, close) => (
						<MenuItem
							key={link.to}
							onClick={() => {
								close();
								vm.handleNavClick(link);
							}}
						>
							<ListItemIcon>{decorateIconWithBadge(link.icon, vm.getItemBadgeState(link.to))}</ListItemIcon>
							<ListItemText primary={link.text} />
						</MenuItem>
					)}
				/>
			);
		}

		const onClick = item.kind === "nav" ? () => vm.handleNavClick(item.link) : vm.handleLogout;
		const rawIcon = item.kind === "nav" ? item.link.icon : <LogoutIcon />;
		const label = item.kind === "nav" ? item.link.text : "Logout";

		// Decorate the icon with an issue-count badge for any nav row whose path has a fetcher
		// in IssueCountsContext (spinner while fetching, the number when > 0, hidden when 0).
		const navPath = item.kind === "nav" ? item.link.to : undefined;
		const icon = decorateIconWithBadge(rawIcon, vm.getItemBadgeState(navPath));

		if (inMenu) {
			return (
				<MenuItem key={item.key} onClick={onClick}>
					<ListItemIcon>{icon}</ListItemIcon>
					<ListItemText primary={label} />
				</MenuItem>
			);
		}

		return (
			<ListItem key={item.key} disablePadding>
				<Tooltip title={showExpanded ? "" : label} placement="right">
					<ListItemButton onClick={onClick}>
						<ListItemIcon>{icon}</ListItemIcon>
						<ListItemText primary={label} />
					</ListItemButton>
				</Tooltip>
			</ListItem>
		);
	};

	const visibleTopItems = overflowIndex === null ? vm.topItems : vm.topItems.slice(0, overflowIndex);
	const overflowedItems = overflowIndex === null ? [] : vm.topItems.slice(overflowIndex);

	// Roll up the badges of any badged items hidden in the overflow onto the More icon, so a pending
	// count is visible without opening More. Same three-state badge as the per-item rows above.
	const overflowBadge = vm.getOverflowBadgeState(overflowedItems);
	const moreIcon = decorateIconWithBadge(<MoreHorizIcon />, overflowBadge);

	const navList = (
		<Box ref={contentRef} className="sidebar-content">
			<List className="sidebar-top-list">
				{visibleTopItems.map((item) => renderItem(item, false))}
				{overflowedItems.length > 0 && (
					<ListItem key="more" disablePadding>
						<Tooltip title={showExpanded ? "" : "More"} placement="right">
							<ListItemButton onClick={(e) => setMoreAnchor(e.currentTarget)}>
								<ListItemIcon>{moreIcon}</ListItemIcon>
								<ListItemText primary="More" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
				)}
			</List>
			{!isMobile && (
				<List ref={ghostRef} className="sidebar-ghost-list" aria-hidden>
					{vm.topItems.map((item) => renderItem(item, false))}
				</List>
			)}
			<Box ref={bottomRef} className="sidebar-bottom-block">
				<List>
					<ListItem key="feedback" disablePadding>
						<Tooltip title={showExpanded ? "" : "Feedback"} placement="right">
							<ListItemButton onClick={vm.handleOpenFeedback}>
								<ListItemIcon>
									<FeedbackIcon />
								</ListItemIcon>
								<ListItemText primary="Feedback" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
				</List>
			</Box>
			<Menu
				anchorEl={moreAnchor}
				open={Boolean(moreAnchor)}
				onClose={() => setMoreAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				{overflowedItems.map((item) => renderItem(item, true))}
			</Menu>
		</Box>
	);

	if (isMobile) {
		return (
			<Drawer
				variant="temporary"
				anchor="left"
				open={props.mobileOpen}
				onClose={props.onMobileClose}
				className="sidebar-drawer expanded sidebar-drawer-mobile"
			>
				{navList}
			</Drawer>
		);
	}

	return (
		<Drawer
			variant="permanent"
			anchor="left"
			className={drawerClass}
			onMouseEnter={() => setExpanded(true)}
			onMouseLeave={() => setExpanded(false)}
		>
			{navList}
		</Drawer>
	);
};
